import { useCallback, useEffect, useRef, useState } from 'react'
import { putLocalSampleRow, type LocalSampleRow } from '@/lib/localSamples'
import {
  hasNativeAudioPlugin,
  nativeRequestMicPermission,
  nativeStartRecording,
  nativeStopRecording,
  subscribeRecordingElapsed,
} from '@/lib/nativeAudio'
import { playCountIn, countInDurationSec } from '@/lib/countIn'
import { getSharedAudioContext } from '@/hooks/useAudioEngine'
import {
  useSequencerStore,
  selectCurrentProject,
} from '@/stores/sequencerStore'

/**
 * Capture the system-default microphone and persist the result into
 * the local sample store.
 *
 * Runtime split:
 *   - **Web** (Chrome / Safari / Firefox on desktop + mobile, plus
 *     iPad / iOS WKWebView) — `MediaRecorder` + `getUserMedia`.
 *   - **Mac Catalyst** — native Capacitor plugin
 *     (`BusswerksAudio.startRecording`). WKWebView on Catalyst hides
 *     `navigator.mediaDevices` on custom-scheme origins, so web
 *     capture is dark there; the native path writes a 16-bit LPCM
 *     WAV via AVAudioEngine and ships the bytes back as base64.
 *
 * Both paths resolve the same `AudioRecordingResult` shape + auto-
 * save to IndexedDB, so call sites don't branch.
 *
 * Owns its own capture session — does NOT depend on the Monitor
 * toggle's stream. A user can record without monitoring to avoid
 * feedback, or monitor while recording (once P3 lands on native).
 *
 * No device-id selection yet. Both paths pick the OS-level default
 * input, which is what we want for MVP.
 */

/**
 * Pick the first MediaRecorder-supported MIME type from a preference
 * list. Prefers high-quality Opus when available (Chrome / Firefox),
 * falls back to AAC on Safari, ultimately leaves it up to the browser
 * via an empty string.
 */
function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return ''
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ]
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c
  }
  return ''
}

export interface AudioRecordingResult {
  /** UUID-ish id under which the recording was saved. */
  sampleId: string
  /** Human-readable default name (e.g. "Take 2026-04-20 15:37"). */
  name: string
  /** Duration in seconds, measured from MediaRecorder start to stop. */
  durationSec: number
  /** The raw bytes — Blob in memory. Already persisted to IndexedDB. */
  blob: Blob
}

export interface UseAudioRecordingResult {
  /** True while MediaRecorder is actively capturing. */
  isRecording: boolean
  /** Elapsed seconds in the current take, live-updated. */
  elapsedSec: number
  /** True while the count-in metronome is running and the real
   *  recording hasn't started yet. UI uses this to distinguish
   *  "counting in" from "recording" — the pad pulse + REC button
   *  should show a different label during the pre-roll. */
  countingIn: boolean
  /** Seconds remaining in the count-in pre-roll, updated ~10 Hz.
   *  Drops to 0 the moment actual recording starts. Always 0 when
   *  `countingIn` is false. */
  countInRemainingSec: number
  /** Last error from start/stop. Cleared when a new recording starts. */
  error: string | null
  /** Whether the runtime supports `MediaRecorder` + `getUserMedia`. */
  supported: boolean
  /**
   * Begin recording. Prompts for mic permission on first call.
   * Accepts an optional `countInBars` for a BPM-synced pre-roll
   * metronome that plays N bars of clicks before capture actually
   * starts (see `lib/countIn.ts`). When omitted or 0, recording
   * starts immediately like before.
   */
  start: (opts?: { countInBars?: number }) => Promise<void>
  /** Finalise + persist the take. Rejects when not currently recording. */
  stop: () => Promise<AudioRecordingResult>
  /** Abandon the current take without saving. Idempotent. */
  cancel: () => void
}

interface UseAudioRecordingOpts {
  /** Called after a successful stop() so a parent can refresh its
   *  sample list / switch to the new take / etc. */
  onRecordingComplete?: (result: AudioRecordingResult) => void
}

function rid(): string {
  return (
    'rec_' +
    Math.random().toString(36).slice(2, 10) +
    Math.random().toString(36).slice(2, 10)
  )
}

export function useAudioRecording(
  opts: UseAudioRecordingOpts = {},
): UseAudioRecordingResult {
  const [isRecording, setIsRecording] = useState(false)
  const [elapsedSec, setElapsedSec] = useState(0)
  const [countingIn, setCountingIn] = useState(false)
  const [countInRemainingSec, setCountInRemainingSec] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const startTimeMsRef = useRef<number>(0)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const countInTickRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const mimeRef = useRef<string>('')
  const nativeActiveRef = useRef(false)
  const nativeElapsedUnsubRef = useRef<(() => void) | null>(null)
  const onCompleteRef = useRef(opts.onRecordingComplete)
  useEffect(() => {
    onCompleteRef.current = opts.onRecordingComplete
  })

  // Native capture wins when the Capacitor plugin is present; web
  // MediaRecorder fills in for every other runtime. We treat either
  // path as "supported" so the button lights up on Catalyst even
  // though `navigator.mediaDevices` is undefined there.
  const nativeSupported = hasNativeAudioPlugin()
  const webSupported =
    typeof navigator !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia) &&
    typeof MediaRecorder !== 'undefined'
  const supported = nativeSupported || webSupported

  const teardown = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current)
      tickRef.current = null
    }
    if (countInTickRef.current) {
      clearInterval(countInTickRef.current)
      countInTickRef.current = null
    }
    if (nativeElapsedUnsubRef.current) {
      nativeElapsedUnsubRef.current()
      nativeElapsedUnsubRef.current = null
    }
    nativeActiveRef.current = false
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop()
      } catch {
        /* recorder already stopped */
      }
    }
    recorderRef.current = null
    if (streamRef.current) {
      for (const t of streamRef.current.getTracks()) t.stop()
      streamRef.current = null
    }
    chunksRef.current = []
    setIsRecording(false)
    setElapsedSec(0)
    setCountingIn(false)
    setCountInRemainingSec(0)
  }, [])

  useEffect(() => () => teardown(), [teardown])

  const start = useCallback(
    async (opts: { countInBars?: number } = {}) => {
      if (!supported) {
        setError('Recording requires MediaRecorder + getUserMedia support.')
        throw new Error('unsupported')
      }
      if (isRecording) return
      setError(null)
      // Count-in metronome (optional). Plays N bars of clicks at the
      // current project BPM BEFORE the real capture begins, so the
      // user can perform to a reliable downbeat. Skipped when `bars`
      // is 0 / missing, or when the AudioContext hasn't been warmed
      // yet (count-in scheduling needs a live context).
      const countInBars = opts.countInBars ?? 0
      if (countInBars > 0) {
        const ctx = getSharedAudioContext()
        if (ctx) {
          const bpm =
            selectCurrentProject(useSequencerStore.getState())?.bpm ?? 120
          const totalSec = countInDurationSec(bpm, countInBars)
          // Drive the public `countingIn` + `countInRemainingSec`
          // state while the pre-roll plays so the UI can show a
          // countdown. 100 ms tick keeps the display smooth without
          // being wasteful.
          setCountingIn(true)
          setCountInRemainingSec(totalSec)
          const startedMs = performance.now()
          countInTickRef.current = setInterval(() => {
            const elapsed = (performance.now() - startedMs) / 1000
            const remaining = Math.max(0, totalSec - elapsed)
            setCountInRemainingSec(remaining)
          }, 100)
          try {
            await playCountIn(ctx, bpm, countInBars)
          } finally {
            if (countInTickRef.current) {
              clearInterval(countInTickRef.current)
              countInTickRef.current = null
            }
            setCountInRemainingSec(0)
            setCountingIn(false)
          }
        }
      }
      if (nativeSupported) {
        try {
          // Prompt once — AVFoundation memoizes, so subsequent starts
          // won't show the system dialog again. A denied result rejects
          // the same way a web permission denial does.
          const { granted } = await nativeRequestMicPermission()
          if (!granted) {
            throw new Error(
              'Microphone permission denied — enable in System Settings ▸ Privacy & Security ▸ Microphone.',
            )
          }
          await nativeStartRecording()
          nativeActiveRef.current = true
          startTimeMsRef.current = performance.now()
          setElapsedSec(0)
          // Native pushes elapsed + peak every 250 ms. Subscribe before
          // setIsRecording flips so the UI sees 0s → first native tick.
          nativeElapsedUnsubRef.current = subscribeRecordingElapsed(e => {
            setElapsedSec(e.elapsedSec)
          })
          setIsRecording(true)
          return
        } catch (err) {
          const message =
            err instanceof Error
              ? err.message
              : 'Native recording failed to start'
          setError(message)
          teardown()
          throw err
        }
      }
      try {
        // System-default input — deliberately no deviceId constraint.
        // When device selection lands (native plugin or web
        // enumerateDevices flow), we'll route the chosen id here.
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          },
        })
        streamRef.current = stream
        const mime = pickMimeType()
        mimeRef.current = mime
        const recorder = mime
          ? new MediaRecorder(stream, { mimeType: mime })
          : new MediaRecorder(stream)
        recorder.ondataavailable = e => {
          if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
        }
        recorder.start()
        recorderRef.current = recorder
        chunksRef.current = []
        startTimeMsRef.current = performance.now()
        tickRef.current = setInterval(() => {
          setElapsedSec((performance.now() - startTimeMsRef.current) / 1000)
        }, 100)
        setIsRecording(true)
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Microphone permission denied'
        setError(message)
        teardown()
        throw err
      }
    },
    [supported, nativeSupported, isRecording, teardown],
  )

  const stop = useCallback(async (): Promise<AudioRecordingResult> => {
    let blob: Blob
    let durationSec: number
    if (nativeActiveRef.current) {
      const result = await nativeStopRecording()
      // Native returns base64 — decode to a Uint8Array, wrap in a
      // Blob with the announced mimeType. `atob` handles standard
      // base64 (no URL-safe variant) which matches what Swift's
      // `base64EncodedString` produces.
      const binary = atob(result.data)
      const bytes = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
      blob = new Blob([bytes], { type: result.mimeType })
      durationSec = result.durationSec
      if (nativeElapsedUnsubRef.current) {
        nativeElapsedUnsubRef.current()
        nativeElapsedUnsubRef.current = null
      }
      nativeActiveRef.current = false
    } else {
      const recorder = recorderRef.current
      if (!recorder || recorder.state === 'inactive') {
        throw new Error('not recording')
      }
      // MediaRecorder flushes the final chunk asynchronously; await
      // the `stop` event before assembling.
      blob = await new Promise((resolve, reject) => {
        recorder.onerror = e => reject(e)
        recorder.onstop = () => {
          const mime = mimeRef.current || 'audio/webm'
          resolve(new Blob(chunksRef.current, { type: mime }))
        }
        try {
          recorder.stop()
        } catch (err) {
          reject(err)
        }
      })
      durationSec = (performance.now() - startTimeMsRef.current) / 1000
      const stream = streamRef.current
      if (stream) {
        for (const t of stream.getTracks()) t.stop()
      }
      streamRef.current = null
      recorderRef.current = null
    }
    if (tickRef.current) clearInterval(tickRef.current)
    tickRef.current = null
    setIsRecording(false)
    setElapsedSec(0)

    // Persist to the local sample store so the rest of the app sees
    // it via the same path as user-uploaded samples.
    const sampleId = rid()
    const now = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    const name =
      `Take ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
      ` ${pad(now.getHours())}:${pad(now.getMinutes())}`
    const row: LocalSampleRow = {
      id: sampleId,
      name,
      contentType: blob.type || 'audio/webm',
      sizeBytes: blob.size,
      durationSec,
      bytes: blob,
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
    }
    try {
      await putLocalSampleRow(row)
    } catch (err) {
      // If the IndexedDB write fails (quota exceeded, private
      // browsing restrictions), we still return the blob so the
      // caller can download it. The save error surfaces via
      // `error` so UI can report the partial-success state.
      setError(
        err instanceof Error
          ? `Recorded but not saved: ${err.message}`
          : 'Recorded but failed to save',
      )
    }
    const result: AudioRecordingResult = { sampleId, name, durationSec, blob }
    try {
      onCompleteRef.current?.(result)
    } catch {
      /* user callback shouldn't take the hook down */
    }
    return result
  }, [])

  const cancel = useCallback(() => {
    teardown()
    chunksRef.current = []
  }, [teardown])

  return {
    isRecording,
    elapsedSec,
    countingIn,
    countInRemainingSec,
    error,
    supported,
    start,
    stop,
    cancel,
  }
}
